/**
 * RolesAdmin.jsx — Admin ▸ Roles.
 *
 * SuperAdmin editor for CUSTOM roles: a role = an Entra appRole value + a
 * governed definition ({ capabilities, scope, enabled }) stored in the
 * 'roleDefinitions' admin_config key. Built-in roles render as locked
 * reference rows (capabilities come from code — ROLE_CAPABILITIES) so new
 * roles can be calibrated against known ones; their seed entries pass through
 * every publish untouched.
 *
 * Persists via onPublish('roleDefinitions', merged, { publish: true }) — the
 * governed admin_config write (publish gate + audit hash-chain). The server
 * re-validates; the guards here mirror its rules (implications, no built-in
 * collision, immutable key/value) for instant feedback.
 *
 * Notifications are deliberately NOT part of a role (see roles_logic.md D1) —
 * the footer deep-links to the per-person rules in Admin ▸ Notifications.
 */
import React, { useState, useEffect, useMemo, useRef } from 'react';
import RoleHolders from './RoleHolders.jsx';
import { useAppConfig } from '../contexts/AppConfigContext.jsx';
import { ROLE_LABELS, ROLE_CAPABILITIES, BUILT_IN_ROLE_PRIORITIES } from '../auth/roles.js';
import { getAccessUsers, createEntraAppRole } from '../api/client.js';
import { getTherapieMapsData } from '../api/blobClient.js';
import {
  COUNTRY_GROUPS, normalizeCapabilities, CUSTOM_ROLE_BASE_CAPABILITIES, EMPTY_CAPABILITIES,
  SESSION_LOCK_INACTIVITY_MIN_MS, SESSION_LOCK_INACTIVITY_MAX_MS,
  SESSION_LOCK_HIDDEN_GRACE_MIN_MS, SESSION_LOCK_HIDDEN_GRACE_MAX_MS,
} from '@shared/roleSchema.js';
import SearchableSelect from './SearchableSelect.jsx';
import { IntegrityVerifier, AuditPanel, PublishConfirmModal } from './governedConfigChrome.jsx';

// Display names for the country-group scope options (values come from the shared
// COUNTRY_GROUPS constant so new groups — e.g. US — appear here automatically).
const COUNTRY_GROUP_LABELS = { IOI: 'Island of Ireland', GB: 'Great Britain', US: 'United States' };

// SuperAdmin stays locked. Supervisor + Admin stay in the built-in rail; every
// other shipped role lives in the governed/custom rail (editable priority).
const BUILT_IN_VALUES = Object.keys(ROLE_CAPABILITIES).filter(r => r !== 'Unauthorized');
const LOCKED_BUILTIN = 'SuperAdmin';
const CORE_BUILTIN_VALUES = ['Supervisor', 'Admin'];
const GOVERNED_BUILTIN_VALUES = ['SupervisorView', 'HoRO', 'ClinicManager', 'ReadOnly'];
const EDITABLE_BUILTIN_VALUES = [...CORE_BUILTIN_VALUES, ...GOVERNED_BUILTIN_VALUES];
const BUILT_IN_SET = new Set(BUILT_IN_VALUES.map(v => v.toLowerCase()));
const GOVERNED_BUILTIN_SET = new Set(GOVERNED_BUILTIN_VALUES);

function sortByPriority(roles) {
  return [...roles].sort((a, b) => {
    const pa = a.priority ?? BUILT_IN_ROLE_PRIORITIES[a.value] ?? 999;
    const pb = b.priority ?? BUILT_IN_ROLE_PRIORITIES[b.value] ?? 999;
    if (pa !== pb) return pa - pb;
    return String(a.label || a.value || '').localeCompare(String(b.label || b.value || ''));
  });
}

function defaultScopeFor(value) {
  if (value === 'HoRO') return { type: 'assignedRegion' };
  if (value === 'ClinicManager') return { type: 'assignedClinics' };
  return { type: 'all' };
}

function migrateScope(value, scope) {
  const s = scope || defaultScopeFor(value);
  if (value === 'HoRO' && (!s.type || s.type === 'all')) return { type: 'assignedRegion' };
  if (value === 'ClinicManager' && (!s.type || s.type === 'all')) return { type: 'assignedClinics' };
  return s;
}

// Fail-closed base — custom capabilities overlay onto all-false, except the
// Knowledge guide which is deliberately opt-out (CUSTOM_ROLE_BASE_CAPABILITIES).
// Derived from the shared key list so a new capability can never be silently
// missed here (normalizeCapabilities iterates the same list) — the drift the
// plan warns about is now impossible by construction rather than by a test.
const EMPTY_CAPS = { ...EMPTY_CAPABILITIES };

function capsFromMatrix(roleValue) {
  const m = ROLE_CAPABILITIES[roleValue];
  return m ? { ...EMPTY_CAPS, ...m } : { ...EMPTY_CAPS };
}

// Hydrate stored capabilities through the SHARED normalizer so the editor
// shows exactly what the runtime resolves: built-ins default missing keys
// from their matrix row (keys added after the row was saved keep code intent
// — the reportView lesson), customs from the fail-closed custom base; legacy
// reportView migrates to dashboardView. Saving then bakes the same values.
function hydrateCaps(caps, roleValue) {
  const base = ROLE_CAPABILITIES[roleValue] ? capsFromMatrix(roleValue) : CUSTOM_ROLE_BASE_CAPABILITIES;
  return normalizeCapabilities(caps || {}, base);
}

function hydrateBuiltInEntry(seed) {
  const value = seed.value;
  return {
    ...seed,
    builtIn: true,
    enabled: seed.enabled !== false,
    label: seed.label || ROLE_LABELS[value] || value,
    priority: seed.priority ?? BUILT_IN_ROLE_PRIORITIES[value],
    capabilities: hydrateCaps(seed.capabilities, value),
    scope: migrateScope(value, seed.scope),
  };
}

// Built-in detection is by value, not just the flag, so a stray entry named
// after a built-in can never surface as an editable custom role.
function isBuiltInEntry(r) {
  return r?.builtIn === true || BUILT_IN_SET.has(String(r?.value || '').toLowerCase());
}

function uid() {
  return 'custom-' + Math.random().toString(36).slice(2, 10);
}

function uniq(arr) {
  return [...new Set((arr || []).filter(Boolean))];
}

// ── Draft normalisation / validation (mirrors the server validator) ──────────

function normalizeScope(s) {
  if (!s || !s.type || s.type === 'all') return { type: 'all' };
  if (s.type === 'assignedRegion' || s.type === 'assignedClinics') return { type: s.type };
  if (s.type === 'countryGroup') return { type: 'countryGroup', value: COUNTRY_GROUPS.includes(s.value) ? s.value : COUNTRY_GROUPS[0] };
  return { type: s.type, value: Array.isArray(s.value) ? s.value : [] };
}

function normalizeSessionLock(sl) {
  // Keep only meaningful fields; an all-blank override is dropped entirely so the
  // role inherits the code defaults (matches the server: absent = inherit).
  if (!sl || typeof sl !== 'object') return undefined;
  const out = {};
  if (Number.isInteger(sl.inactivityMs))  out.inactivityMs  = sl.inactivityMs;
  if (Number.isInteger(sl.hiddenGraceMs)) out.hiddenGraceMs = sl.hiddenGraceMs;
  if (sl.exempt === true)                 out.exempt        = true;
  return Object.keys(out).length ? out : undefined;
}

function normalizeCustom(r) {
  // Shared normalizer (write/reportView migration + implications) so a saved
  // row and a hydrated draft normalise identically — otherwise the diff view
  // would show phantom changes for keys added after the row was saved.
  const c = hydrateCaps(r.capabilities, r.value);
  if (c.costEdit)    c.costView = true;
  if (c.createTags)  c.assignTags = true;
  const capabilities = {};
  for (const k of Object.keys(EMPTY_CAPS)) capabilities[k] = !!c[k];
  return {
    key:          r.key,
    value:        String(r.value || '').trim(),
    label:        String(r.label || '').trim(),
    description:  String(r.description || '').trim(),
    enabled:      r.enabled !== false,
    builtIn:      !!r.builtIn,
    priority:     Number.isInteger(r.priority)
      ? r.priority
      : (BUILT_IN_ROLE_PRIORITIES[r.value] ?? undefined),
    capabilities,
    scope:        normalizeScope(migrateScope(r.value, r.scope)),
    sessionLock:  normalizeSessionLock(r.sessionLock),
  };
}

function validateCustoms(customs) {
  const errors = [];
  const seen = new Set();
  for (const r of customs) {
    const name = r.label?.trim() || r.value?.trim() || 'New role';
    if (!String(r.label || '').trim()) errors.push(`${name}: display label is required.`);
    const v = String(r.value || '').trim();
    if (!v) {
      errors.push(`${name}: role value (the Entra appRole string) is required.`);
    } else {
      if (!/^[A-Za-z][A-Za-z0-9._-]*$/.test(v)) {
        errors.push(`${name}: role value must start with a letter and use only letters, digits, . _ - (no spaces).`);
      }
      if (!r.builtIn && BUILT_IN_SET.has(v.toLowerCase())) errors.push(`${name}: “${v}” collides with a built-in role.`);
      if (seen.has(v.toLowerCase())) errors.push(`Role value “${v}” is used by more than one role.`);
      seen.add(v.toLowerCase());
    }
    const s = r.scope || {};
    if (s.type === 'countryGroup' && !COUNTRY_GROUPS.includes(s.value)) errors.push(`${name}: pick a country group (${COUNTRY_GROUPS.join(', ')}) for the country-group scope.`);
    if (!r.builtIn && s.type === 'regions' && !(Array.isArray(s.value) && s.value.length))  errors.push(`${name}: pick at least one region.`);
    if (!r.builtIn && s.type === 'clinics' && !(Array.isArray(s.value) && s.value.length))  errors.push(`${name}: pick at least one clinic.`);
    if (!Number.isInteger(r.priority)) errors.push(`${name}: priority is required (lower = higher privilege).`);
    else if (r.priority < 0 || r.priority > 999) errors.push(`${name}: priority must be 0–999.`);

    const sl = r.sessionLock;
    if (sl && typeof sl === 'object') {
      const inMin = SESSION_LOCK_INACTIVITY_MIN_MS / 60000, inMax = SESSION_LOCK_INACTIVITY_MAX_MS / 60000;
      const grMin = SESSION_LOCK_HIDDEN_GRACE_MIN_MS / 1000, grMax = SESSION_LOCK_HIDDEN_GRACE_MAX_MS / 1000;
      if (sl.inactivityMs != null && (sl.inactivityMs < SESSION_LOCK_INACTIVITY_MIN_MS || sl.inactivityMs > SESSION_LOCK_INACTIVITY_MAX_MS)) {
        errors.push(`${name}: auto-lock time must be ${inMin}–${inMax} minutes.`);
      }
      if (sl.hiddenGraceMs != null && (sl.hiddenGraceMs < SESSION_LOCK_HIDDEN_GRACE_MIN_MS || sl.hiddenGraceMs > SESSION_LOCK_HIDDEN_GRACE_MAX_MS)) {
        errors.push(`${name}: hidden-tab lock time must be ${grMin}–${grMax} seconds.`);
      }
    }
  }
  return errors;
}

// ── Human-readable diffs (publish modal + audit history) ─────────────────────

function scopeText(scope) {
  const s = scope || { type: 'all' };
  if (s.type === 'assignedRegion') return 'Assigned region (per person, TherapieMaps)';
  if (s.type === 'assignedClinics') return 'Assigned clinics (per person, TherapieMaps)';
  if (s.type === 'countryGroup') return COUNTRY_GROUP_LABELS[s.value] || 'Country group (unset)';
  if (s.type === 'regions') return `Regions: ${(s.value || []).join(', ') || 'none'}`;
  if (s.type === 'clinics') return `Clinics: ${(s.value || []).length} selected`;
  return 'All cases';
}

// Row-vocabulary states — the same words the matrix shows, so diffs read like
// the UI ("Costs Hidden → View"), not like raw booleans.
function capStates(caps) {
  const c = { ...EMPTY_CAPS, ...(caps || {}) };
  return {
    'Submit case':      c.submitCase ? 'yes' : 'no',
    'Edit case':        c.editCase ? 'yes' : 'no',
    'Status & code':    c.changeStatusAndCode ? 'yes' : 'no',
    'Resolve':          c.resolve ? 'on' : 'off',
    'Reopen':           c.reopen ? 'on' : 'off',
    'Delete':           c.del ? 'on' : 'off',
    'Costs':            c.costEdit ? 'Edit' : c.costView ? 'View' : 'Hidden',
    'Dashboard':        c.dashboardView ? 'View' : 'Hidden',
    'Knowledge':        c.knowledgeView ? 'Shown' : 'Hidden',
    'Reports':          c.caseReportView ? 'Allowed' : 'Hidden',
    'Comments':         c.comment ? 'on' : 'off',
    'Private comments': c.privateView ? 'Visible' : 'Hidden',
    'Tags':             c.createTags ? 'Manage' : c.assignTags ? 'Assign' : 'None',
    'Requirements':     [c.manageReq && 'Add', c.respondReq && 'Respond', c.waiveReq && 'Waive'].filter(Boolean).join(' + ') || 'off',
    'Response SLA':     c.manageSla ? 'Manage' : 'off',
    'Assume identity':  c.impersonate ? 'on' : 'off',
    'Clinic approvals': c.manageClinicAccess ? 'Allow' : 'Hidden',
    'Share a case':     c.shareCaseAccess ? 'Allow' : 'Hidden',
  };
}

function describeRoleChange(prev, next) {
  const parts = [];
  if (prev.label !== next.label)             parts.push(`renamed “${prev.label}” → “${next.label}”`);
  if (prev.description !== next.description) parts.push('description updated');
  if (prev.priority !== next.priority)       parts.push(`priority ${prev.priority ?? '—'} → ${next.priority ?? '—'}`);
  if ((prev.enabled !== false) !== (next.enabled !== false)) parts.push(next.enabled !== false ? 'enabled' : 'disabled');
  // Normalise both sides so keys added after `prev` was saved don't read as changes.
  const ps = capStates(normalizeCustom(prev).capabilities), ns = capStates(normalizeCustom(next).capabilities);
  for (const k of Object.keys(ps)) if (ps[k] !== ns[k]) parts.push(`${k} ${ps[k]} → ${ns[k]}`);
  if (scopeText(prev.scope) !== scopeText(next.scope)) parts.push(`scope ${scopeText(prev.scope)} → ${scopeText(next.scope)}`);
  if (sessionLockText(prev.sessionLock) !== sessionLockText(next.sessionLock)) {
    parts.push(`auto-lock ${sessionLockText(prev.sessionLock)} → ${sessionLockText(next.sessionLock)}`);
  }
  return parts.join(' · ') || 'updated';
}

// Human-readable summary of a role's session-lock override for the diff view.
function sessionLockText(sl) {
  if (sl?.exempt) return 'exempt (manual only)';
  const parts = [];
  if (Number.isInteger(sl?.inactivityMs))  parts.push(`${Math.round(sl.inactivityMs / 60000)} min idle`);
  if (Number.isInteger(sl?.hiddenGraceMs)) parts.push(`${Math.round(sl.hiddenGraceMs / 1000)} s hidden`);
  return parts.length ? parts.join(' / ') : 'defaults';
}

function diffCustomRoles(saved, draft) {
  const byKey = (list) => new Map(list.map(r => [r.key, r]));
  const a = byKey(saved), b = byKey(draft);
  const name = (r) => r.label?.trim() || r.value?.trim() || r.key;
  const lines = [];
  for (const [k, r] of b) if (!a.has(k)) lines.push(`New role — ${name(r)} (${r.value || 'no value'}) · ${scopeText(r.scope)}`);
  for (const [k, r] of a) if (!b.has(k)) lines.push(`Removed — ${name(r)} (${r.value})`);
  for (const [k, next] of b) {
    const prev = a.get(k);
    if (!prev) continue;
    if (JSON.stringify(normalizeCustom(prev)) === JSON.stringify(normalizeCustom(next))) continue;
    lines.push(`${name(next)}: ${describeRoleChange(prev, next)}`);
  }
  return lines;
}

// Audit summariser — renders the server's diffRoleDefinitions shape
// (changed: [{ id, label, fields: { name: { from, to } } }]) as
// "DirectorOps: capabilities.costEdit false → true"; tolerant of older shapes.
function summarizeRoleDiff(d) {
  const diff = d || {};
  const name  = (x) => (typeof x === 'string' ? x : x?.label || x?.value || x?.key || 'role');
  const short = (v) => (v == null ? '—' : (typeof v === 'object' ? JSON.stringify(v) : String(v)));
  const parts = [];
  if (diff.added?.length)   parts.push(`added ${diff.added.map(name).join(', ')}`);
  if (diff.removed?.length) parts.push(`removed ${diff.removed.map(name).join(', ')}`);
  for (const c of diff.changed || []) {
    if (typeof c === 'string') { parts.push(`${c} changed`); continue; }
    const fields = Object.entries(c?.fields || {})
      .map(([f, v]) => `${f} ${short(v?.from)} → ${short(v?.to)}`);
    parts.push(`${name(c)}: ${fields.join(', ')
      || c?.summary || (Array.isArray(c?.changes) ? c.changes.join(', ') : 'changed')}`);
  }
  return parts.length ? parts.join(' · ') : 'no field changes';
}

// ── Small controls ────────────────────────────────────────────────────────────

/** Segmented state control — same look as AdminFilterChip, plus disabled. */
function Seg({ value, options, onChange, disabled }) {
  return (
    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
      {options.map(o => (
        <button
          key={o.value}
          type="button"
          className={`btn sm${value === o.value ? ' primary' : ' ghost'}`}
          onClick={() => onChange(o.value)}
          disabled={disabled}
          style={{ fontSize: 11, padding: '5px 10px' }}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function CapCheck({ label, checked, onChange, disabled }) {
  return (
    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: disabled ? 'var(--text-3)' : 'var(--text)' }}>
      <input type="checkbox" checked={checked} disabled={disabled} onChange={e => onChange(e.target.checked)}
        style={{ accentColor: 'var(--brand)', width: 15, height: 15 }} />
      {label}
    </label>
  );
}

/** Immutable monospace value chip (same as the old RoleDefinitionsSection). */
function ValueChip({ children }) {
  return (
    <span style={{
      fontSize: 11, background: 'var(--surface-3)', color: 'var(--text-3)',
      padding: '1px 7px', borderRadius: 4, fontFamily: 'monospace', flexShrink: 0,
    }}>{children}</span>
  );
}

// ── Layout helpers ────────────────────────────────────────────────────────────

function RoleSection({ title, description, children, first }) {
  return (
    <div style={{
      borderTop: first ? 'none' : '1px solid var(--border)',
      paddingTop: first ? 0 : 18,
      marginTop: first ? 0 : 4,
    }}>
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{title}</div>
        {description && (
          <div className="field-hint" style={{ marginTop: 3, maxWidth: 640, lineHeight: 1.45 }}>{description}</div>
        )}
      </div>
      {children}
    </div>
  );
}

function CapRow({ label, hint, children }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(140px, 180px) 1fr', gap: '10px 20px', alignItems: 'start', marginBottom: 10 }}>
      <div>
        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)' }}>{label}</div>
        {hint && <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 2, lineHeight: 1.35 }}>{hint}</div>}
      </div>
      <div>{children}</div>
    </div>
  );
}

// ── Capability matrix — grouped sections ──────────────────────────────────────

function CapabilityMatrix({ caps, onChange, readOnly }) {
  const c = { ...EMPTY_CAPS, ...(caps || {}) };
  const set = (patch) => { if (!readOnly && onChange) onChange({ ...c, ...patch }); };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <RoleSection
        title="Cases"
        description="What holders can do on case records. Submit creates new cases; Edit changes details and attachments; Status & code covers severity and workflow status (not Mark resolved)."
        first
      >
        <CapRow label="Actions">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <CapCheck label="Submit case" checked={c.submitCase} disabled={readOnly}
              onChange={v => set({ submitCase: v, write: v || c.editCase })} />
            <CapCheck label="Edit case" checked={c.editCase} disabled={readOnly}
              onChange={v => set({ editCase: v, write: c.submitCase || v, ...(v ? {} : { del: false }) })} />
            <CapCheck label="Change status and code" checked={c.changeStatusAndCode} disabled={readOnly}
              onChange={v => set({ changeStatusAndCode: v })} />
          </div>
        </CapRow>
        <CapRow label="Close & reopen" hint="Mark resolved closes with a reason. Reopen is typically Admin+.">
          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
            <CapCheck label="Resolve" checked={c.resolve} disabled={readOnly} onChange={v => set({ resolve: v })} />
            <CapCheck label="Reopen"  checked={c.reopen}  disabled={readOnly} onChange={v => set({ reopen: v })} />
          </div>
        </CapRow>
        <CapRow label="When edit case" hint="Requires Edit case above.">
          <CapCheck label="Delete" checked={c.del} disabled={readOnly || !c.editCase} onChange={v => set({ del: v })} />
        </CapRow>
        <CapRow
          label="Requirements"
          hint="Add = create/remove requirements on a case (admin/supervisor tier). Respond = upload the requested file or complete a sign-off — for responder roles (Clinic Manager / HoRO / service teams), no Edit case needed. Waive = record, with a reason, that a required document does not exist (e.g. no consent form) and withdraw that record — Supervisor tier by default."
        >
          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
            <CapCheck
              label="Add requirements"
              checked={c.manageReq}
              disabled={readOnly}
              onChange={v => set({ manageReq: v })}
            />
            <CapCheck
              label="Respond to requirements"
              checked={c.respondReq}
              disabled={readOnly}
              onChange={v => set({ respondReq: v })}
            />
            <CapCheck
              label="Can waive a required document"
              checked={!!c.waiveReq}
              disabled={readOnly}
              onChange={v => set({ waiveReq: v })}
            />
            <CapCheck
              label="Can manage response SLA"
              checked={!!c.manageSla}
              disabled={readOnly}
              onChange={v => set({ manageSla: v })}
            />
          </div>
        </CapRow>
      </RoleSection>

      <RoleSection
        title="Financial & reporting"
        description="Costs are per-case spend. Dashboard is the analytics page (stats scoped to the role's cases). Reports is the case-file Report tab — printable case report plus attachment downloads. Knowledge is the in-app user guide."
      >
        <CapRow label="Costs">
          <Seg
            disabled={readOnly}
            value={c.costEdit ? 'edit' : c.costView ? 'view' : 'hidden'}
            options={[{ value: 'hidden', label: 'Hidden' }, { value: 'view', label: 'View' }, { value: 'edit', label: 'Edit' }]}
            onChange={v => set(
              v === 'edit'   ? { costView: true,  costEdit: true }
              : v === 'view' ? { costView: true,  costEdit: false }
              :                { costView: false, costEdit: false })}
          />
        </CapRow>
        <CapRow label="Dashboard">
          <Seg
            disabled={readOnly}
            value={c.dashboardView ? 'view' : 'hidden'}
            options={[{ value: 'hidden', label: 'Hidden' }, { value: 'view', label: 'View' }]}
            onChange={v => set({ dashboardView: v === 'view' })}
          />
        </CapRow>
        <CapRow label="Reports" hint="Case-file Report tab: PDF export + file/ZIP downloads.">
          <Seg
            disabled={readOnly}
            value={c.caseReportView ? 'allowed' : 'hidden'}
            options={[{ value: 'hidden', label: 'Hidden' }, { value: 'allowed', label: 'Allowed' }]}
            onChange={v => set({ caseReportView: v === 'allowed' })}
          />
        </CapRow>
        <CapRow label="Knowledge">
          <Seg
            disabled={readOnly}
            value={c.knowledgeView ? 'shown' : 'hidden'}
            options={[{ value: 'hidden', label: 'Hidden' }, { value: 'shown', label: 'Shown' }]}
            onChange={v => set({ knowledgeView: v === 'shown' })}
          />
        </CapRow>
      </RoleSection>

      <RoleSection
        title="Collaboration"
        description="Comments on cases. Private comments need Post comments to write; Visible only controls whether they can read private threads."
      >
        <CapRow label="Comments">
          <CapCheck label="Post comments" checked={c.comment} disabled={readOnly}
            onChange={v => set({ comment: v })} />
        </CapRow>
        <CapRow label="Private comments">
          <Seg
            disabled={readOnly}
            value={c.privateView ? 'visible' : 'hidden'}
            options={[{ value: 'hidden', label: 'Hidden' }, { value: 'visible', label: 'Visible' }]}
            onChange={v => set({ privateView: v === 'visible' })}
          />
        </CapRow>
        <CapRow label="Tags" hint="Manage includes creating new tag definitions.">
          <Seg
            disabled={readOnly}
            value={c.createTags ? 'manage' : c.assignTags ? 'assign' : 'none'}
            options={[{ value: 'none', label: 'None' }, { value: 'assign', label: 'Assign' }, { value: 'manage', label: 'Manage' }]}
            onChange={v => set(
              v === 'manage'   ? { assignTags: true,  createTags: true }
              : v === 'assign' ? { assignTags: true,  createTags: false }
              :                  { assignTags: false, createTags: false })}
          />
        </CapRow>
      </RoleSection>

      <RoleSection
        title="Administration"
        description="Assume identity is a read-only view-as tool for troubleshooting."
      >
        <CapRow label="Assume identity" hint="Targets need a higher priority number. Nothing saves while assuming.">
          <CapCheck label="Allow" checked={c.impersonate} disabled={readOnly} onChange={v => set({ impersonate: v })} />
        </CapRow>
      </RoleSection>

      <RoleSection
        title="Clinic access"
        description="Managing shared clinic-mailbox staff (the FOH sub-identities). Both are scoped to this role's own clinics — a Clinic Manager to their clinic, a HoRO to their region, a country role to its country, a Supervisor to all."
      >
        <CapRow label="Approvals inbox" hint="Confirm FOH names and action PIN resets from the Notifications inbox, for clinics in scope.">
          <CapCheck label="Allow" checked={c.manageClinicAccess} disabled={readOnly} onChange={v => set({ manageClinicAccess: v })} />
        </CapRow>
        <CapRow label="Share a case" hint="Grant a registered clinic staff member temporary access to one case (ends on close, optional day cap).">
          <CapCheck label="Allow" checked={c.shareCaseAccess} disabled={readOnly} onChange={v => set({ shareCaseAccess: v })} />
        </CapRow>
      </RoleSection>
    </div>
  );
}

// ── Scope picker — compiles to the two enforcement primitives server-side ─────

function ScopePicker({ scope, onChange, roleValue, regionOptions, clinicOptions, directoryLoaded }) {
  const s = normalizeScope(scope);
  const lockedPerPerson = roleValue === 'HoRO' ? 'assignedRegion'
    : roleValue === 'ClinicManager' ? 'assignedClinics'
      : null;

  if (lockedPerPerson) {
    return (
      <div style={{
        padding: '10px 12px', borderRadius: 8, background: 'var(--surface-2)',
        border: '1px solid var(--border)', fontSize: 12.5, color: 'var(--text-2)', maxWidth: 520,
      }}>
        {scopeText({ type: lockedPerPerson })}
        <div className="field-hint" style={{ marginTop: 6 }}>
          Resolved from each holder's TherapieMaps mapping — not a fixed clinic or region list.
        </div>
      </div>
    );
  }

  const setType = (type) => {
    if (type === s.type) return;
    if (type === 'all') onChange({ type: 'all' });
    else if (type === 'countryGroup') onChange({ type: 'countryGroup', value: COUNTRY_GROUPS[0] });
    else onChange({ type, value: [] });
  };
  // Keep already-saved values selectable even if the directory list has moved on.
  const withCurrent = (opts) => uniq([...opts, ...(Array.isArray(s.value) ? s.value : [])]).sort();

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <Seg
        value={s.type}
        options={[
          { value: 'all', label: 'All cases' },
          { value: 'countryGroup', label: 'Country group' },
          { value: 'regions', label: 'Regions' },
          { value: 'clinics', label: 'Clinics' },
        ]}
        onChange={setType}
      />

      {s.type === 'countryGroup' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <Seg
            value={s.value || COUNTRY_GROUPS[0]}
            options={COUNTRY_GROUPS.map(g => ({ value: g, label: g }))}
            onChange={v => onChange({ type: 'countryGroup', value: v })}
          />
          <div className="field-hint">
            IOI = island of Ireland (ROI + Northern Ireland) · GB = England, Scotland &amp; Wales · US = United States.
            Resolved from the clinic directory’s raw country — never from the case country field.
          </div>
        </div>
      )}

      {s.type === 'regions' && (
        <div style={{ maxWidth: 420 }}>
          <SearchableSelect
            multiple
            value={Array.isArray(s.value) ? s.value : []}
            onChange={e => onChange({ type: 'regions', value: e.target.value })}
            options={withCurrent(regionOptions).map(r => ({ value: r, label: r }))}
            placeholder="Pick regions…"
          />
          {!directoryLoaded && <div className="field-hint">Clinic directory not loaded — region list may be incomplete.</div>}
        </div>
      )}

      {s.type === 'clinics' && (
        <div style={{ maxWidth: 420 }}>
          <SearchableSelect
            multiple
            value={Array.isArray(s.value) ? s.value : []}
            onChange={e => onChange({ type: 'clinics', value: e.target.value })}
            options={withCurrent(clinicOptions).map(c => ({ value: c, label: c }))}
            placeholder="Pick clinics…"
          />
          {!directoryLoaded && <div className="field-hint">Clinic directory not loaded — clinic list may be incomplete.</div>}
        </div>
      )}
    </div>
  );
}

// ── Right panel: built-in reference (locked) ──────────────────────────────────

function BuiltInPanel({ name, seedEntry }) {
  const caps = { ...ROLE_CAPABILITIES[name] };
  if (caps.privateView === undefined) caps.privateView = name !== 'ClinicManager';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxWidth: 720 }}>
      <div style={{ marginBottom: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 15, fontWeight: 600, color: 'var(--text)' }}>{ROLE_LABELS[name] || name}</span>
          <ValueChip>{name}</ValueChip>
          <span className="count-chip" style={{ background: 'var(--surface-2)', color: 'var(--text-3)' }}>built-in · locked</span>
        </div>
        {seedEntry?.description && <div className="field-hint" style={{ marginTop: 6 }}>{seedEntry.description}</div>}
      </div>

      <RoleSection title="Scope" description="Where this role can see cases." first>
        <div style={{ fontSize: 12.5, color: 'var(--text-2)' }}>{scopeText(defaultScopeFor(name))}</div>
      </RoleSection>

      <RoleSection title="Capabilities" description="Reference matrix — calibrate custom roles against these defaults.">
        <CapabilityMatrix caps={caps} readOnly />
      </RoleSection>
    </div>
  );
}

// ── Right panel: custom role editor ───────────────────────────────────────────

function CustomRolePanel({
  role, onChange, onDelete, valueLocked, isBuiltIn = false, isCoreBuiltIn = false,
  entraValues, unclaimedValues, regionOptions, clinicOptions, directoryLoaded,
  onCreateInEntra, createInEntraBusy, createInEntraError,
}) {
  const set = (patch) => onChange({ ...role, ...patch });
  const v = String(role.value || '').trim();
  const inEntra = entraValues ? entraValues.has(v) : null;
  const dlId = `role-value-options-${role.key}`;
  const isGovernedShipped = isBuiltIn && GOVERNED_BUILTIN_SET.has(role.value);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxWidth: 720 }}>
      {/* Header */}
      <div style={{ marginBottom: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 15, fontWeight: 600, color: 'var(--text)' }}>
            {role.label?.trim() || ROLE_LABELS[role.value] || 'New role'}
          </span>
          {v && <ValueChip>{v}</ValueChip>}
          {isGovernedShipped && (
            <span className="count-chip" style={{ background: 'var(--surface-2)', color: 'var(--text-3)' }}>shipped</span>
          )}
          {role.enabled === false && (
            <span className="count-chip" style={{ background: 'var(--surface-2)', color: 'var(--warn, #b45309)' }}>disabled</span>
          )}
        </div>
      </div>

      <RoleSection
        title="Identity"
        description="How this role appears in the admin UI and maps to the Entra app role string."
        first
      >
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
          {!isBuiltIn && (
            <label>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)', marginBottom: 4 }}>Display label</div>
              <input type="text" className="input" value={role.label || ''} maxLength={80}
                onChange={e => set({ label: e.target.value })}
                placeholder="e.g. Director of Operations"
                style={{ fontSize: 13, padding: '7px 10px', width: '100%' }} />
            </label>
          )}
          <label style={{ gridColumn: isBuiltIn ? '1 / -1' : undefined }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)', marginBottom: 4 }}>Description</div>
            <input type="text" className="input" value={role.description || ''} maxLength={200}
              onChange={e => set({ description: e.target.value })}
              placeholder="What this role is for"
              style={{ fontSize: 13, padding: '7px 10px', width: '100%' }} />
          </label>
        </div>

        {!isBuiltIn && (
          <div style={{ marginTop: 12 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)', marginBottom: 4 }}>Role value (Entra appRole)</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              {valueLocked ? (
                <ValueChip>{v || '—'}</ValueChip>
              ) : (
                <>
                  <input type="text" className="input" list={dlId} value={role.value || ''} maxLength={64}
                    onChange={e => set({ value: e.target.value })}
                    placeholder="e.g. DirectorOps"
                    style={{ fontSize: 13, padding: '7px 10px', fontFamily: 'monospace', flex: '0 0 220px' }} />
                  <datalist id={dlId}>
                    {unclaimedValues.map(u => <option key={u} value={u} />)}
                  </datalist>
                </>
              )}
              {v && inEntra === true  && <span className="count-chip" style={{ background: 'var(--surface-2)', color: 'var(--ok)' }}>✓ in Entra</span>}
              {v && inEntra === false && (
                <>
                  <span className="count-chip" style={{ background: 'var(--surface-2)', color: 'var(--warn, #b45309)' }}>⚠ not in Entra yet</span>
                  <button type="button" className="btn ghost sm" onClick={onCreateInEntra}
                    disabled={createInEntraBusy} style={{ fontSize: 11 }}>
                    {createInEntraBusy ? 'Creating…' : 'Create in Entra'}
                  </button>
                </>
              )}
            </div>
            {createInEntraError && (
              <div className="callout danger" style={{ fontSize: 12, marginTop: 6 }}>{createInEntraError}</div>
            )}
            <div className="field-hint" style={{ marginTop: 4 }}>
              {valueLocked
                ? 'The value is fixed after first publish — Entra assignments reference it.'
                : 'Must match an Entra app role for Users & Security assignments.'}
            </div>
          </div>
        )}
      </RoleSection>

      <RoleSection
        title="Access control"
        description="Priority sets the impersonation hierarchy (lower = more privileged). Disable a role to deny all holders immediately."
      >
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 20, alignItems: 'flex-start' }}>
          <CapCheck
            label="Role enabled"
            checked={role.enabled !== false}
            onChange={val => set({ enabled: val })}
          />
          <label style={{ minWidth: 120 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)', marginBottom: 4 }}>Priority</div>
            {isCoreBuiltIn ? (
              <div style={{ fontSize: 13, fontFamily: 'monospace', color: 'var(--text-2)', padding: '7px 0' }}>
                {role.priority ?? BUILT_IN_ROLE_PRIORITIES[role.value] ?? '—'}
              </div>
            ) : (
              <input
                type="number"
                className="input"
                min={0}
                max={999}
                step={1}
                value={Number.isInteger(role.priority) ? role.priority : ''}
                onChange={e => {
                  const n = parseInt(e.target.value, 10);
                  set({ priority: Number.isNaN(n) ? undefined : n });
                }}
                placeholder="e.g. 25"
                style={{ fontSize: 13, padding: '7px 10px', width: 100 }}
              />
            )}
            <div className="field-hint" style={{ marginTop: 4, maxWidth: 280 }}>
              0 Super Admin · 1 Supervisor · 2 Admin · 20 HoRO · 30 CM
            </div>
          </label>
        </div>
      </RoleSection>

      {role.value !== 'SuperAdmin' && (() => {
        const sl = role.sessionLock || {};
        const setSL = (patch) => {
          const next = { ...sl, ...patch };
          for (const k of Object.keys(next)) if (next[k] == null) delete next[k];
          if (next.exempt !== true) delete next.exempt;
          set({ sessionLock: Object.keys(next).length ? next : undefined });
        };
        const exempt = !!sl.exempt;
        return (
          <RoleSection
            title="Session lock"
            description="When the PIN screen re-locks for this role. Blank inherits the defaults (15 min idle · 30 s hidden tab). Exempt = holders lock manually only, like SuperAdmin."
          >
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 20, alignItems: 'flex-start' }}>
              <CapCheck
                label="Exempt from auto-lock"
                checked={exempt}
                onChange={val => setSL({ exempt: val ? true : null })}
              />
              <label style={{ minWidth: 120, opacity: exempt ? 0.5 : 1 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)', marginBottom: 4 }}>Auto-lock after (min)</div>
                <input
                  type="number" className="input" disabled={exempt}
                  min={SESSION_LOCK_INACTIVITY_MIN_MS / 60000} max={SESSION_LOCK_INACTIVITY_MAX_MS / 60000} step={1}
                  value={Number.isInteger(sl.inactivityMs) ? Math.round(sl.inactivityMs / 60000) : ''}
                  onChange={e => { const n = parseInt(e.target.value, 10); setSL({ inactivityMs: Number.isNaN(n) ? null : n * 60000 }); }}
                  placeholder="15"
                  style={{ fontSize: 13, padding: '7px 10px', width: 100 }}
                />
              </label>
              <label style={{ minWidth: 120, opacity: exempt ? 0.5 : 1 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)', marginBottom: 4 }}>Lock hidden tab after (s)</div>
                <input
                  type="number" className="input" disabled={exempt}
                  min={SESSION_LOCK_HIDDEN_GRACE_MIN_MS / 1000} max={SESSION_LOCK_HIDDEN_GRACE_MAX_MS / 1000} step={1}
                  value={Number.isInteger(sl.hiddenGraceMs) ? Math.round(sl.hiddenGraceMs / 1000) : ''}
                  onChange={e => { const n = parseInt(e.target.value, 10); setSL({ hiddenGraceMs: Number.isNaN(n) ? null : n * 1000 }); }}
                  placeholder="30"
                  style={{ fontSize: 13, padding: '7px 10px', width: 100 }}
                />
              </label>
            </div>
          </RoleSection>
        );
      })()}

      <RoleSection
        title="Scope"
        description="Which cases holders see. Country group compiles to regions from the clinic directory."
      >
        <ScopePicker
          scope={role.scope}
          roleValue={role.value}
          onChange={scope => set({ scope })}
          regionOptions={regionOptions}
          clinicOptions={clinicOptions}
          directoryLoaded={directoryLoaded}
        />
      </RoleSection>

      <RoleSection
        title="Capabilities"
        description="What holders can do within their scope."
      >
        <CapabilityMatrix caps={role.capabilities} onChange={capabilities => set({ capabilities })} />
      </RoleSection>

      <div style={{ borderTop: '1px solid var(--border)', paddingTop: 14, marginTop: 8 }}>
        {!isBuiltIn && (
          <button type="button" className="btn ghost sm danger" onClick={onDelete}>
            Delete role
          </button>
        )}
        <span style={{ fontSize: 11.5, color: 'var(--text-3)', marginLeft: isBuiltIn ? 0 : 8 }}>
          {isBuiltIn ? 'Shipped role — publish to apply changes.' : 'Takes effect on publish (~60s).'}
        </span>
      </div>
    </div>
  );
}

// ── Main editor ───────────────────────────────────────────────────────────────

export default function RolesAdmin({ config, onPublish, onOpenNotifications }) {
  const all = Array.isArray(config?.roleDefinitions) ? config.roleDefinitions : [];
  const seedByKey = useMemo(() => new Map(all.filter(isBuiltInEntry).map(r => [r.key, r])), [all]);

  const savedCoreBuiltIns = useMemo(
    () => CORE_BUILTIN_VALUES.map(value => {
      const seed = all.find(r => r.value === value) || { key: value.toLowerCase(), value, label: ROLE_LABELS[value] };
      return hydrateBuiltInEntry(seed);
    }),
    [all],
  );
  const savedGovernedAndCustom = useMemo(() => {
    const governed = GOVERNED_BUILTIN_VALUES.map(value => {
      const seed = all.find(r => r.value === value) || { key: value.toLowerCase(), value, label: ROLE_LABELS[value] };
      return hydrateBuiltInEntry(seed);
    });
    const pure = all.filter(r => !isBuiltInEntry(r)).map(r => ({
      ...r,
      scope: normalizeScope(r.scope),
      capabilities: hydrateCaps(r.capabilities, r.value),
    }));
    return sortByPriority([...governed, ...pure]);
  }, [all]);
  const savedKeys = useMemo(
    () => new Set(savedGovernedAndCustom.filter(r => !r.builtIn).map(r => r.key)),
    [savedGovernedAndCustom],
  );

  const [builtIns, setBuiltIns]   = useState(() => savedCoreBuiltIns.map(r => ({ ...r })));
  const [customs, setCustoms]     = useState(() => savedGovernedAndCustom.map(r => ({ ...r })));
  const [selected, setSelected]   = useState('builtin:' + LOCKED_BUILTIN);

  const savedCoreJson = JSON.stringify(savedCoreBuiltIns);
  const savedCustomJson  = JSON.stringify(savedGovernedAndCustom);
  const seededCoreRef = useRef(savedCoreJson);
  const seededCustomRef  = useRef(savedCustomJson);
  useEffect(() => {
    if (savedCoreJson === seededCoreRef.current && savedCustomJson === seededCustomRef.current) return;
    const prevCoreJson = seededCoreRef.current;
    const prevCustomJson = seededCustomRef.current;
    setBuiltIns(prev => (JSON.stringify(prev) === prevCoreJson ? savedCoreBuiltIns.map(r => ({ ...r })) : prev));
    // An untouched list follows the server. A draft is kept, but roles someone
    // else published meanwhile are carried into it — otherwise publishing the
    // draft would delete them (how Trainee HoRO GB was lost, 2026-10-08).
    setCustoms(prev => {
      if (JSON.stringify(prev) === prevCustomJson) return savedGovernedAndCustom.map(r => ({ ...r }));
      const before = new Set(JSON.parse(prevCustomJson).map(r => r.key));
      const have = new Set(prev.map(r => r.key));
      const theirs = savedGovernedAndCustom.filter(r => !before.has(r.key) && !have.has(r.key));
      return theirs.length ? [...prev, ...theirs.map(r => ({ ...r }))] : prev;
    });
    seededCoreRef.current = savedCoreJson;
    seededCustomRef.current = savedCustomJson;
  }, [savedCoreJson, savedCustomJson]); // eslint-disable-line react-hooks/exhaustive-deps

  const [busy, setBusy]           = useState(false);
  const [error, setError]         = useState(null);
  const [notice, setNotice]       = useState(null);
  const [showAudit, setShowAudit] = useState(false);
  const [auditToken, setAuditToken] = useState(0);
  const [previewing, setPreviewing] = useState(false);
  // Someone else published Roles after this page loaded (server 409).
  const [stale, setStale]         = useState(null);
  const { reloadConfig } = useAppConfig();

  // Entra appRole catalog (same source Users & Security uses) — for the live
  // '✓ in Entra' badge and the unclaimed-value suggestions.
  const [entraRoles, setEntraRoles] = useState(null);
  const [entraCreateBusy, setEntraCreateBusy] = useState(false);
  const [entraCreateError, setEntraCreateError] = useState(null);
  // Clinic directory — regions/clinics scope value dropdowns.
  const [tmData, setTmData] = useState(null);

  async function refreshEntraCatalog() {
    const d = await getAccessUsers();
    setEntraRoles(d?.roles || []);
    return d;
  }

  useEffect(() => {
    let cancelled = false;
    getAccessUsers().then(d => { if (!cancelled) setEntraRoles(d?.roles || []); }).catch(() => {});
    getTherapieMapsData().then(d => { if (!cancelled) setTmData(d); }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const entraValues = useMemo(() => (entraRoles ? new Set(entraRoles.map(r => r.value)) : null), [entraRoles]);
  const regionOptions = useMemo(() => uniq((tmData?.clinics || []).map(c => c.region)).sort(), [tmData]);
  const clinicOptions = useMemo(() => uniq((tmData?.clinics || []).map(c => c.name)).sort(), [tmData]);

  const dirty  = JSON.stringify(builtIns) !== JSON.stringify(savedCoreBuiltIns)
    || JSON.stringify(customs) !== JSON.stringify(savedGovernedAndCustom);
  const errors = [...validateCustoms(builtIns), ...validateCustoms(customs)];
  const diffLines = [
    ...diffCustomRoles(savedCoreBuiltIns, builtIns),
    ...diffCustomRoles(savedGovernedAndCustom, customs),
  ];

  const sortedCustoms = useMemo(() => sortByPriority(customs), [customs]);

  const selectedCustom = selected.startsWith('custom:')
    ? customs.find(r => 'custom:' + r.key === selected)
    : null;
  const selectedCoreBuiltIn = selected.startsWith('builtin:') && selected.slice(8) !== LOCKED_BUILTIN
    ? builtIns.find(r => r.value === selected.slice(8))
    : null;
  const selectedLockedBuiltIn = selected === 'builtin:' + LOCKED_BUILTIN ? LOCKED_BUILTIN : null;

  const setBuiltInAt = (value, next) => setBuiltIns(builtIns.map(r => (r.value === value ? next : r)));
  const setCustomAt = (key, next) => setCustoms(customs.map(r => (r.key === key ? next : r)));
  function addRole() {
    const key = uid();
    setCustoms([...customs, {
      key, value: '', label: '', description: '', enabled: true, builtIn: false,
      priority: 25,
      capabilities: { ...EMPTY_CAPS, ...CUSTOM_ROLE_BASE_CAPABILITIES }, scope: { type: 'all' },
    }]);
    setSelected('custom:' + key);
  }
  // Entra app roles nobody has defined here yet. Users & Security lists every
  // Entra role, so one created straight in Entra (or via "Create in Entra" on a
  // draft that was never published) could be assigned there but was missing
  // from this rail — and its holders are denied sign-in until it is defined.
  const entraOnly = useMemo(() => {
    if (!entraRoles) return [];
    const defined = new Set(customs.map(r => String(r.value || '').trim().toLowerCase()));
    return entraRoles
      .filter(r => r.value && !BUILT_IN_SET.has(r.value.toLowerCase()) && !defined.has(r.value.toLowerCase()))
      .sort((a, b) => (a.label || a.value).localeCompare(b.label || b.value));
  }, [entraRoles, customs]);
  function setUpEntraRole(er) {
    const key = uid();
    setCustoms([...customs, {
      key, value: er.value, label: er.label || er.value, description: '', enabled: true, builtIn: false,
      priority: 25,
      capabilities: { ...EMPTY_CAPS, ...CUSTOM_ROLE_BASE_CAPABILITIES }, scope: { type: 'all' },
    }]);
    setSelected('custom:' + key);
    setEntraCreateError(null);
  }
  function deleteRole(key) {
    setCustoms(customs.filter(r => r.key !== key));
    setSelected('builtin:' + CORE_BUILTIN_VALUES[0]);
    setEntraCreateError(null);
  }
  function discard() {
    setBuiltIns(savedCoreBuiltIns.map(r => ({ ...r })));
    setCustoms(savedGovernedAndCustom.map(r => ({ ...r })));
    setPreviewing(false);
    setSelected('builtin:' + LOCKED_BUILTIN);
  }
  async function publish() {
    setBusy(true); setError(null);
    try {
      const normalizedCore = builtIns.map(r => normalizeCustom({ ...r, builtIn: true }));
      const normalizedCustoms = customs.map(r => normalizeCustom(
        r.builtIn ? { ...r, builtIn: true } : r,
      ));
      await onPublish('roleDefinitions', [...normalizedCore, ...normalizedCustoms], { publish: true });
      setBuiltIns(normalizedCore);
      setCustoms(sortByPriority(normalizedCustoms));
      setPreviewing(false);
      setAuditToken(t => t + 1);
      setNotice('Roles published. Live within ~60 seconds (server config cache).');
      setTimeout(() => setNotice(null), 4000);
    } catch (e) {
      if (e?.status === 409 && e?.data?.stale) {
        const at = e.data.changedAt ? new Date(e.data.changedAt).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' }) : null;
        setPreviewing(false);
        setStale(`Roles were changed by ${e.data.changedBy || 'someone else'}${at ? ` on ${at}` : ''}, after you opened this page. Load the latest to bring their changes in, then publish again.`);
      }
      else setError(e?.message || 'Failed to publish roles.');
    } finally {
      setBusy(false);
    }
  }
  async function loadLatest() {
    setBusy(true); setError(null);
    try {
      await reloadConfig();
      setStale(null);
      setNotice('Loaded the latest roles. Your unpublished edits are kept — review and publish again.');
      setTimeout(() => setNotice(null), 6000);
    } catch (e) {
      setError(e?.message || 'Could not load the latest roles.');
    } finally {
      setBusy(false);
    }
  }

  async function createInEntra(role) {
    const value = String(role.value || '').trim();
    if (!value) return;
    setEntraCreateBusy(true);
    setEntraCreateError(null);
    try {
      const result = await createEntraAppRole({
        value,
        displayName: role.label,
        description: role.description,
      });
      await refreshEntraCatalog();
      setNotice(result.created
        ? 'App role created in Entra.'
        : 'App role already exists in Entra.');
      setTimeout(() => setNotice(null), 4000);
    } catch (e) {
      setEntraCreateError(e?.message || 'Failed to create app role in Entra.');
    } finally {
      setEntraCreateBusy(false);
    }
  }

  // Unclaimed appRole values (exist in Entra, no definition yet) for the current draft.
  const unclaimedFor = (role) => {
    if (!entraRoles) return [];
    const used = new Set(customs.filter(r => r.key !== role.key).map(r => String(r.value || '').toLowerCase()));
    return entraRoles
      .map(r => r.value)
      .filter(x => x && !BUILT_IN_SET.has(x.toLowerCase()) && !used.has(x.toLowerCase()))
      .sort();
  };

  const railBtn = (active, muted) => ({
    display: 'flex', alignItems: 'center', gap: 8, width: '100%', textAlign: 'left',
    padding: '7px 10px', borderRadius: 8, cursor: 'pointer',
    border: active ? '1px solid var(--brand)' : '1px solid var(--border)',
    background: active ? 'var(--surface-2)' : 'var(--surface)',
    opacity: muted && !active ? 0.6 : 1,
  });
  const railLabel = { fontSize: 12.5, fontWeight: 600, color: 'var(--text)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };
  const railHead = { fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: 'var(--text-3)', margin: '4px 0' };
  const railPrio = { fontSize: 10, fontFamily: 'monospace', color: 'var(--text-3)', flexShrink: 0 };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {error  && <div className="callout danger" style={{ fontSize: 13 }}>{error}</div>}
      {notice && <div className="callout brand" style={{ fontSize: 13 }}>{notice}</div>}

      <div className="admin-section-head">
        <div>
          <div className="admin-section-title">
            Roles
            {dirty && <span className="badge" style={{ marginLeft: 8, fontSize: 10 }}>Draft</span>}
          </div>
          <div className="admin-section-desc">
            Custom roles: an Entra app role + capabilities + scope. Entra decides WHO holds a role;
            this tab decides WHAT it can see and do, and WHERE.
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
          <button type="button" className="btn ghost sm" onClick={() => setShowAudit(s => !s)} style={{ fontSize: 11 }}>
            {showAudit ? 'Hide history' : 'History'}
          </button>
          {dirty && (
            <button type="button" className="btn primary sm" onClick={() => setPreviewing(true)} disabled={busy || errors.length > 0}
              title={errors.length ? 'Fix validation errors first' : 'Review and publish'} style={{ fontSize: 12 }}>
              Review &amp; publish
            </button>
          )}
        </div>
      </div>

      {stale && (
        <div className="callout danger" style={{ fontSize: 12, alignItems: 'center' }}>
          <div style={{ flex: 1 }}>
            <strong>Not published.</strong> {stale}
          </div>
          <button type="button" className="btn sm" onClick={loadLatest} disabled={busy} style={{ fontSize: 11, flexShrink: 0 }}>
            {busy ? 'Loading…' : 'Load latest'}
          </button>
        </div>
      )}

      {dirty && errors.length > 0 && (
        <div className="callout danger" style={{ fontSize: 12 }}>
          <div style={{ flex: 1 }}>
            <strong>Cannot publish yet:</strong>
            <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
              {errors.map((e, i) => <li key={i}>{e}</li>)}
            </ul>
          </div>
        </div>
      )}

      {showAudit && (
        <div style={{ padding: '12px 14px', border: '1px solid var(--border)', borderRadius: 10, background: 'var(--surface-2)' }}>
          <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8 }}>Change history</div>
          <IntegrityVerifier />
          <AuditPanel configKey="roleDefinitions" refreshToken={auditToken} summarizeDiff={summarizeRoleDiff} />
        </div>
      )}

      {/* Left rail (role list) + right panel (selected role) */}
      <div style={{ display: 'flex', gap: 18, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <div style={{ flex: '0 0 240px', display: 'flex', flexDirection: 'column', gap: 4 }}>
          <div style={railHead}>Built-in roles</div>
          <button key={LOCKED_BUILTIN} type="button" onClick={() => setSelected('builtin:' + LOCKED_BUILTIN)}
            style={railBtn(selectedLockedBuiltIn === LOCKED_BUILTIN, true)}>
            <span style={railLabel}>{ROLE_LABELS[LOCKED_BUILTIN]}</span>
            <span style={railPrio}>0</span>
            <span className="count-chip" style={{ background: 'var(--surface-2)', color: 'var(--text-3)' }}>locked</span>
          </button>
          {builtIns.map(r => (
            <button key={r.value} type="button" onClick={() => setSelected('builtin:' + r.value)}
              style={railBtn(selectedCoreBuiltIn?.value === r.value, false)}>
              <span style={railLabel}>{r.label?.trim() || ROLE_LABELS[r.value]}</span>
              <span style={railPrio}>{r.priority ?? '—'}</span>
              {r.enabled === false && <span className="count-chip" style={{ background: 'var(--surface-2)', color: 'var(--text-3)' }}>off</span>}
            </button>
          ))}

          <div style={{ ...railHead, marginTop: 10 }}>Roles (by priority)</div>
          {sortedCustoms.length === 0 && <div className="field-hint">No roles yet.</div>}
          {sortedCustoms.map(r => (
            <button key={r.key} type="button" onClick={() => { setSelected('custom:' + r.key); setEntraCreateError(null); }}
              style={railBtn(selectedCustom?.key === r.key, false)}>
              <span style={railLabel}>{r.label?.trim() || r.value?.trim() || 'New role'}</span>
              <span style={railPrio}>{r.priority ?? '—'}</span>
              {r.builtIn && <span className="count-chip" style={{ background: 'var(--surface-2)', color: 'var(--text-3)', fontSize: 10 }}>shipped</span>}
              {r.enabled === false && <span className="count-chip" style={{ background: 'var(--surface-2)', color: 'var(--text-3)' }}>off</span>}
            </button>
          ))}
          <div style={{ marginTop: 6 }}>
            <button type="button" className="btn ghost sm" onClick={addRole} style={{ fontSize: 12 }}>+ New role</button>
          </div>

          {entraOnly.length > 0 && (
            <>
              <div style={{ ...railHead, marginTop: 10 }}>In Entra — not set up</div>
              <div className="field-hint" style={{ marginBottom: 2 }}>
                Assignable in Users &amp; Security, but holders can’t sign in until the role is set up here.
              </div>
              {entraOnly.map(er => (
                <button key={er.value} type="button" onClick={() => setUpEntraRole(er)}
                  title={`Set up ${er.label || er.value}: pick its capabilities and scope, then publish`}
                  style={{ ...railBtn(false, false), borderStyle: 'dashed' }}>
                  <span style={railLabel}>{er.label || er.value}</span>
                  <span className="count-chip" style={{ background: 'var(--surface-2)', color: 'var(--warn, #b45309)', fontSize: 10 }}>set up</span>
                </button>
              ))}
            </>
          )}
        </div>

        <div style={{ flex: '1 1 520px', minWidth: 360 }}>
          {selectedCustom ? (
            <CustomRolePanel
              role={selectedCustom}
              onChange={next => setCustomAt(selectedCustom.key, next)}
              onDelete={() => deleteRole(selectedCustom.key)}
              valueLocked={savedKeys.has(selectedCustom.key)}
              isBuiltIn={!!selectedCustom.builtIn}
              entraValues={entraValues}
              unclaimedValues={unclaimedFor(selectedCustom)}
              regionOptions={regionOptions}
              clinicOptions={clinicOptions}
              directoryLoaded={!!tmData}
              onCreateInEntra={() => createInEntra(selectedCustom)}
              createInEntraBusy={entraCreateBusy}
              createInEntraError={entraCreateError}
            />
          ) : selectedCoreBuiltIn ? (
            <CustomRolePanel
              role={selectedCoreBuiltIn}
              isBuiltIn
              isCoreBuiltIn
              onChange={next => setBuiltInAt(selectedCoreBuiltIn.value, next)}
              valueLocked
              entraValues={entraValues}
              unclaimedValues={[]}
              regionOptions={regionOptions}
              clinicOptions={clinicOptions}
              directoryLoaded={!!tmData}
            />
          ) : (
            <BuiltInPanel name={selectedLockedBuiltIn || LOCKED_BUILTIN} seedEntry={seedByKey.get('super-admin')} />
          )}
        </div>

        {/* Third column: what the role CAN do sits to its left; who HAS it was a
            different screen until now, so comparing the two meant navigating away
            and back. */}
        <div style={{ flex: '0 1 320px', minWidth: 260 }}>
          <RoleHolders
            roleValue={selectedCustom?.value || selectedCoreBuiltIn?.value || selectedLockedBuiltIn || LOCKED_BUILTIN}
            roleLabel={selectedCustom?.label || selectedCoreBuiltIn?.label
              || ROLE_LABELS[selectedLockedBuiltIn || LOCKED_BUILTIN]
              || (selectedLockedBuiltIn || LOCKED_BUILTIN)}
          />
        </div>
      </div>

      {/* No notify column by design (roles_logic.md D1) — rules are per person. */}
      <div style={{ borderTop: '1px solid var(--border)', paddingTop: 10, fontSize: 12, color: 'var(--text-3)' }}>
        Email notifications are configured per person in{' '}
        <a href="#" onClick={e => { e.preventDefault(); onOpenNotifications?.(); }}>Notifications →</a>
      </div>

      <PublishConfirmModal
        open={previewing}
        title="Publish role definitions?"
        description="Built-in roles (except Super Admin) and custom roles gate what their holders can see and do. Changes go live within ~60 seconds."
        publishing={busy}
        hasChanges={diffLines.length > 0}
        canPublish={errors.length === 0}
        onCancel={() => setPreviewing(false)}
        onDiscard={discard}
        onPublish={publish}
      >
        <ul style={{ margin: '12px 0 0', paddingLeft: 18, fontSize: 12, display: 'flex', flexDirection: 'column', gap: 4 }}>
          {diffLines.map((l, i) => <li key={i}>{l}</li>)}
        </ul>
      </PublishConfirmModal>
    </div>
  );
}
